import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ModulePackage, ModulePackageBillingPeriod, ModulePackagePricing} from '@app/interfaces/packages/module-package';
import {ModulePackagesService} from '@app/services/api/module-packages.service';
import {NotificationService} from '@app/services/notification.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {ProductPackageCodeEnum} from '@app/enums/ProductPackageCodeEnum';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {AccountProductPackage} from '@app/interfaces/packages/account-product-package';
import {AvtonetInfoDialogComponent} from '@app/shared/dialogs/avtonet-info-dialog/avtonet-info-dialog.component';
import {CheckoutDialogComponent, CheckoutDialogData, CheckoutDialogResult} from '../checkout-dialog/checkout-dialog.component';

@Component({
  selector: 'app-activate-module-package-dialog',
  templateUrl: './activate-module-package-dialog.component.html',
  styleUrls: ['./activate-module-package-dialog.component.scss'],
  standalone: false
})
export class ActivateModulePackageDialogComponent implements OnInit, OnDestroy {

  /**
   * Fires when the checkout flow this dialog kicked off actually succeeds.
   * activateModule() closes THIS dialog before opening the checkout dialog
   * (so the two never stack), which means its own `afterClosed()` already
   * resolves with `undefined` before checkout even starts — callers that
   * gated a refresh on that result (e.g. module-package-card.component.ts)
   * can no longer rely on it. Subscribe to this output on the component
   * instance instead (`dialogRef.componentInstance.activationSucceeded`).
   */
  @Output() activationSucceeded = new EventEmitter<void>();

  public isLoading = false;
  public modulePackage: ModulePackage;
  public disableButton = false;
  public pricing: ModulePackagePricing | null = null;
  /** CarPeak is always billed per vehicle (BRIEFD-7435) — there is no longer a
   *  cheaper "subscription model" to opt into, so this simply mirrors the module. */
  public get useSubscription(): boolean {
    return this.modulePackage.hasSubscriptionOption;
  }
  public selectedPeriod: ModulePackageBillingPeriod | null = null;
  public displayPrice: string = '';
  public displayPriceWithDdv: string = '';
  public displayBillingUnit: string = '';
  /** "Paket 50 vozil po 4,41 €, nato 4,90 €" — empty when the account has no deal. */
  public displayVolumeDeal: string = '';
  /** Free work-order units left before activation is required (work-order module
   *  only). Null when unknown/not applicable or already unlocked. */
  public freeUnitsRemaining: number | null = null;
  public freeUnitsLimit: number | null = null;
  /** Whether the account's active subscription bills yearly (vs monthly). Drives
   *  the billing-frequency wording in the activation notice. */
  public activePeriodIsYearly = false;
  /** The account's own product package, loaded async in ngOnInit — drives whether the
   *  billing-period toggle should appear for a settled-but-unsigned MINI account's
   *  first module purchase. Undefined until the config call resolves. */
  public accountProductPackage?: AccountProductPackage;

  // Payment modal state
  public showPaymentModal = false;
  public stripeClientSecret: string | null = null;
  public stripePublishableKey: string = ''; // Will be loaded from config
  public stripeDisabled = false; // Account bills manually via Odoo — no card step
  public pendingPackageUuid: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<any>,
    private matDialog: MatDialog,
    private modulePackagesService: ModulePackagesService,
    private notificationService: NotificationService,
    private configApi: ConfigAPIService
  ) {
    this.modulePackage = data.modulePackage;
  }

  ngOnInit(): void {
    // Load pricing tiers if module has subscription option
    if (this.modulePackage.hasSubscriptionOption) {
      this.loadPricing();
    }

    this.updateDisplayPrice();

    // Free-unit quota: prefer the value already loaded on the settings-page card
    // (passed in via dialog data) so we don't repeat the slow request. Only fetch
    // as a fallback when the dialog is opened without it.
    if (this.data.quota !== undefined) {
      this.freeUnitsLimit = this.data.quota?.limit ?? null;
      this.freeUnitsRemaining = this.data.quota?.remaining ?? null;
    } else if (this.modulePackage.includedUnits) {
      this.loadFreeUnitsQuota();
    }

    // Load Stripe publishable key + the account's subscription period from config
    this.configApi.getAccountConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        this.stripePublishableKey = config.stripePublishableKey || '';
        this.stripeDisabled = !!config.stripeDisabled;
        this.accountProductPackage = config.activeAccountProductPackage;
        this.activePeriodIsYearly =
          String(config.activeAccountProductPackage?.subscriptionType) === 'yearly';

        // Now that account state is known, set the toggle's initial selection
        // (mirrors module-package-card.ts's display, which always lists monthly
        // first) and recompute the displayed price/notice for this module.
        if (this.hasBothPeriods) {
          this.selectedPeriod = 'monthly';
        }
        this.updateDisplayPrice();
      });
  }

  /** Fetch the free work-order quota so the modal can show "X of Y free left".
   *  Non-fatal: on error it falls back to the plain includedUnits label. */
  private loadFreeUnitsQuota(): void {
    this.modulePackagesService.getModuleQuota(this.modulePackage.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.freeUnitsLimit = res?.data?.limit ?? null;
          this.freeUnitsRemaining = res?.data?.remaining ?? null; // null once unlocked / no counter
        },
        error: () => {
          this.freeUnitsLimit = null;
          this.freeUnitsRemaining = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPricing(): void {
    this.modulePackagesService.getModulePricing(this.modulePackage.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.pricing = result.data ?? null;
          this.updateDisplayPrice();
        },
        error: (error) => {
          console.error('Error loading pricing:', error);
        }
      });
  }

  /** True when the module has both a monthly and a yearly price, i.e. there's
   *  an actual choice to make (drives whether the period toggle renders). */
  get hasBothPeriods(): boolean {
    const hasBothModulePrices = this.modulePackage.priceMonthly != null && this.modulePackage.priceYearly != null;
    if (!hasBothModulePrices) {
      return false;
    }

    // Every module has both prices, but the choice is only meaningful when this
    // purchase is what actually starts the account's own subscription: a settled
    // MINI account (productCode === MINI) that hasn't signed anything yet. Every
    // other case (already-paying tiers, or a MINI account that already bought a
    // module before) is a plain upsell onto the existing cadence (BRIEFD-6162).
    return this.accountProductPackage?.productPackage?.productCode === ProductPackageCodeEnum.SUBSCRIPTION_MINI
      && !this.accountProductPackage?.isSigned;
  }

  /** True only when the module has a genuine per-unit pricing component
   *  (CarPeak's per-vehicle rate, Work Order's per-work-order rate past its
   *  free quota) — i.e. billing actually depends on how much the account
   *  uses it. False for flat-fee subscription modules like Virtual Salon,
   *  which bill the same price every period regardless of usage. Drives
   *  which billing-frequency notice renders below, since "glede na vašo
   *  uporabo" (based on your usage) is only true for the former. */
  get isUsageBasedBilling(): boolean {
    return this.modulePackage.pricePerUnit != null;
  }

  /** Computed yearly savings vs paying 12x the monthly price, for the
   *  "Prihraniš X €" hint — mirrors module-package-card.component.ts's
   *  own yearlySavings so the two never disagree. */
  get yearlySavings(): number | null {
    const m = this.modulePackage.priceMonthly;
    const y = this.modulePackage.priceYearly;
    if (m == null || y == null) { return null; }
    const saved = (m * 12) - y;
    return saved > 0 ? saved : null;
  }

  selectPeriod(period: ModulePackageBillingPeriod): void {
    this.selectedPeriod = period;
    this.updateDisplayPrice();
  }

  updateDisplayPrice(): void {
    this.displayPrice = this.calculateDisplayPrice();
    this.displayPriceWithDdv = this.calculateDisplayPriceWithDdv();
    this.displayBillingUnit = this.calculateDisplayBillingUnit();
    this.displayVolumeDeal = this.calculateDisplayVolumeDeal();
  }

  /** Spell out the account's negotiated volume deal for the selected tier, so the
   *  discounted rate the dealer is quoted doesn't look like it applies forever. */
  calculateDisplayVolumeDeal(): string {
    if (!this.useSubscription) {
      return '';
    }

    const units = this.pricing?.volume_units;
    const dealPrice = this.pricing?.volume_per_vehicle;

    if (!this.pricing || !units || dealPrice == null) {
      return '';
    }

    const listPrice = this.pricing.per_vehicle;

    return $localize`Paket ${units} vozil po ${dealPrice.toFixed(2)} €, nato ${listPrice.toFixed(2)} € na vozilo`;
  }

  calculateDisplayPrice(): string {
    if (this.modulePackage.isFree) {
      return $localize`Brezplačno`;
    }

    if (!this.useSubscription) {
      if (this.modulePackage.pricePerUnit) {
        return `${this.modulePackage.pricePerUnit.toFixed(2)} €`;
      }
      // When both periods exist, respect the toggle instead of always
      // cascading to monthly — this is what previously made the dialog
      // ignore a yearly selection entirely.
      if (this.hasBothPeriods) {
        const price = this.selectedPeriod === 'yearly' ? this.modulePackage.priceYearly : this.modulePackage.priceMonthly;
        return `${price!.toFixed(2)} €`;
      }
      if (this.modulePackage.priceMonthly != null) {
        return `${this.modulePackage.priceMonthly.toFixed(2)} €`;
      }
      if (this.modulePackage.priceYearly != null) {
        return `${this.modulePackage.priceYearly.toFixed(2)} €`;
      }
      return $localize`Po povpraševanju`;
    } else {
      // Show selected subscription tier price
      if (this.pricing) {
        // A negotiated volume deal is the rate this dealer actually starts paying,
        // so lead with it; displayVolumeDeal spells out where it stops applying.
        const perVehicle = this.pricing.volume_per_vehicle ?? this.pricing.per_vehicle;
        return `${perVehicle.toFixed(2)} €`;
      }
      return $localize`Po povpraševanju`;
    }
  }

  calculateDisplayPriceWithDdv(): string {
    if (this.modulePackage.isFree) {
      return '';
    }

    const ddvMultiplier = 1.22;

    if (!this.useSubscription) {
      if (!this.modulePackage.pricePerUnit) {
        return '';
      }
      return `${(this.modulePackage.pricePerUnit * ddvMultiplier).toFixed(2)} €`;
    } else {
      if (this.pricing) {
        const perVehicle = this.pricing.volume_per_vehicle ?? this.pricing.per_vehicle;
        return `${(perVehicle * ddvMultiplier).toFixed(2)} €`;
      }
      return '';
    }
  }

  calculateDisplayBillingUnit(): string {
    if (!this.useSubscription) {
      if (this.hasBothPeriods) {
        return this.selectedPeriod === 'yearly' ? $localize`na leto` : $localize`na mesec`;
      }
      return this.getBillingUnitLabel();
    } else {
      return $localize`na vozilo`;
    }
  }

  activateModule(): void {
    // In-flight guard: a double-click (or the VIRTUAL_SALON branch's extra
    // getAccountConfig() round-trip below) must never open two stacked
    // CheckoutDialogComponent instances / charge the module twice. Reset on
    // every path that leaves this dialog open for a legitimate retry
    // (config-fetch error, coming-soon redirect); no reset is needed on the
    // success path since doActivateModule() closes this dialog immediately.
    if (this.disableButton) {
      return;
    }
    this.disableButton = true;

    if (this.modulePackage.moduleType === 'VIRTUAL_SALON') {
      this.configApi.getAccountConfig()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (config) => {
            // Mirrors virtual-salon-hero.component.ts's `!isBasicActive && !isProActive`
            // guard: an account that already has real access must never be soft-gated
            // out of managing that access (upgrading tier, changing billing period,
            // reactivating) just because the coming-soon flag is also set on their
            // product package.
            const hasExistingAccess = !!config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_BASIC]
              || !!config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_PRO];
            if (!hasExistingAccess && config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_COMING_SOON]) {
              this.dialogRef.close();
              AvtonetInfoDialogComponent.open(this.matDialog, {
                title: $localize`:@@advertising.virtual-salon.hero.coming-soon.title:Spletna stran kmalu na voljo`,
                intro: $localize`:@@advertising.virtual-salon.hero.coming-soon.intro:Ta funkcionalnost bo kmalu na voljo tudi za vaš račun. Obveščali te bomo, ko bo pripravljena.`,
                steps: [],
              });
              return;
            }
            this.doActivateModule();
          },
          error: () => {
            // Dialog stays open on a failed config fetch — release the guard
            // so the user can retry instead of being permanently blocked.
            this.disableButton = false;
          }
        });
      return;
    }

    this.doActivateModule();
  }

  private doActivateModule(): void {
    const hasSubscription = this.useSubscription && this.modulePackage.hasSubscriptionOption;

    // Close this dialog before opening the checkout step so only one
    // confirmation dialog is ever visible at a time (they used to stack).
    this.dialogRef.close();

    // Open checkout dialog with pricing preview
    const checkoutDialogRef = CheckoutDialogComponent.open(this.matDialog, {
      type: 'module_package',
      modulePackage: this.modulePackage,
      modulePackagePeriod: this.hasBothPeriods ? this.selectedPeriod : null
    } as CheckoutDialogData);

    checkoutDialogRef.afterClosed().subscribe((result: CheckoutDialogResult) => {
      if (result?.success && result?.activated) {
        // this.close(true) here would be a no-op — this dialog's own
        // dialogRef was already closed (with undefined) above, and Angular
        // Material doesn't let a later close() change an already-emitted
        // afterClosed() value. Signal success via the output instead so
        // callers holding the component instance (not just the dialogRef)
        // can still react.
        this.activationSucceeded.emit();
      }
    });
  }

  /**
   * Legacy activation flow (fallback if CheckoutDialog can't be opened)
   */
  private activateModuleLegacy(): void {
    this.disableButton = true;
    this.isLoading = true;

    const hasSubscription = this.useSubscription && this.modulePackage.hasSubscriptionOption;

    // Initialize Stripe payment authorization
    this.modulePackagesService.initializePayment(
      this.modulePackage.uuid,
      hasSubscription,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Show payment modal with Stripe Elements if authorization is required
          // (never for Stripe-disabled accounts — they activate without a card)
          if (!this.stripeDisabled && response.requires_authorization && response.stripe_client_secret) {
            this.stripeClientSecret = response.stripe_client_secret;
            this.showPaymentModal = true;
            this.isLoading = false;
            return;
          }

          // If existing payment method is available, proceed to activate with it
          if (!response.requires_authorization && response.existing_payment_method) {
            // Has existing payment method - call activation endpoint
            this.activateWithExistingPayment(response);
            return;
          }

          // Already activated (e.g., local/testing mode or free package)
          if (response.status === 'activated') {
            this.isLoading = false;
            this.notificationService.showSuccessAlert($localize`Modul aktiviran`);
            this.modulePackagesService.clearCache();
            this.close(true);
            return;
          }

          // Unexpected state
          this.isLoading = false;
          this.notificationService.showErrorAlert($localize`Nepričakovano stanje aktivacije`);
          this.disableButton = false;
        },
        error: (error) => {
          console.error('Error preparing activation:', error);
          this.notificationService.showErrorAlert(
            error.error?.message || $localize`Napaka pri pripravi aktivacije`
          );
          this.disableButton = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Activate module package with existing payment method
   * Called when user already has an authorized card on file
   */
  private activateWithExistingPayment(response: any): void {
    const hasSubscription = this.useSubscription && this.modulePackage.hasSubscriptionOption;

    // Call the activation endpoint with existing payment method
    this.modulePackagesService.activateModulePackage(
      this.modulePackage.uuid,
      hasSubscription,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (activationResult) => {
          this.isLoading = false;
          this.notificationService.showSuccessAlert($localize`Modul aktiviran`);
          this.modulePackagesService.clearCache();
          this.close(true);
        },
        error: (error) => {
          console.error('Error activating module:', error);
          this.notificationService.showErrorAlert(
            error.error?.message || $localize`Napaka pri aktivaciji modula`
          );
          this.disableButton = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Handle payment authorization completion
   * Webhook will handle module activation automatically
   */
  public onPaymentComplete(): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.notificationService.showSuccessAlert($localize`Modul uspešno aktiviran!`);

    // Clear cache and close dialog
    this.modulePackagesService.clearCache();
    this.close(true);
  }

  /**
   * Handle payment cancellation
   */
  public onPaymentCancelled(): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.pendingPackageUuid = null;
    this.disableButton = false;
    this.notificationService.showInfoAlert($localize`Avtorizacija plačila preklicana`);
  }

  /**
   * Handle payment failure that requires a new SetupIntent
   * This is called when the SetupIntent enters a terminal state
   */
  public onPaymentFailed(errorMessage: string): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.pendingPackageUuid = null;
    this.disableButton = false;
    this.notificationService.showErrorAlert(
      errorMessage || $localize`Avtorizacija plačila ni uspela. Prosimo, poskusite znova.`
    );
  }

  close(success: boolean = false): void {
    this.dialogRef.close(success);
  }

  static open(
    dialog: MatDialog,
    modulePackage: ModulePackage,
    quota?: { remaining: number | null; limit: number | null }
  ): MatDialogRef<ActivateModulePackageDialogComponent> {
    return dialog.open(ActivateModulePackageDialogComponent, {
      width: '500px',
      disableClose: false,
      data: {
        modulePackage,
        // undefined = not provided (dialog fetches); an object = reuse the
        // card's already-loaded quota (avoids a second slow request).
        quota
      }
    });
  }

  getFormattedPrice(): string {
    if (this.modulePackage.isFree) {
      return $localize`Brezplačno`;
    }

    if (!this.modulePackage.pricePerUnit) {
      return $localize`Po povpraševanju`;
    }

    return `${this.modulePackage.pricePerUnit.toFixed(2)} €`;
  }

  getBillingUnitLabel(): string {
    if (!this.modulePackage.billingUnit) {
      return '';
    }

    const labels: Record<string, string> = {
      'per_month': $localize`na mesec`,
      'per_vehicle': $localize`na vozilo`,
    };

    return labels[this.modulePackage.billingUnit] || this.modulePackage.billingUnit;
  }
}
